import { useVirtualizer } from '@tanstack/react-virtual';
import { CSSProperties, memo, ReactNode, useEffect, useRef } from 'react';

export const VIRTUALIZER_ITEMS_COMPONENT = 'virtualizer-items';

interface VirtualizerProps {
    items: readonly ReactNode[];
    estimatedSize: number;
    overscan?: number;
    height?: number | 'fit-content';
    scrollToIndex?: number;
    onTotalSizeChange?: (size: number) => void;
    // ResizeObserver loop fix, see https://github.com/TanStack/virtual/pull/923
    preventResizeObserverLoop?: boolean;
}

export const Virtualizer = memo(
    ({
        items,
        estimatedSize,
        overscan = 3,
        scrollToIndex,
        height,
        onTotalSizeChange,
        preventResizeObserverLoop = false,
    }: VirtualizerProps) => {
        const parentRef = useRef<HTMLDivElement>(null);
        const virtualizer = useVirtualizer({
            count: items.length,
            getScrollElement: () => parentRef.current,
            estimateSize: () => estimatedSize,
            overscan,
            useAnimationFrameWithResizeObserver: preventResizeObserverLoop,
        });

        useEffect(() => {
            if (scrollToIndex !== undefined) {
                try {
                    virtualizer.scrollToIndex(scrollToIndex, {
                        align: 'center',
                        behavior: 'smooth',
                    });
                } catch {
                    if (process.env.NODE_ENV === 'development') {
                        // eslint-disable-next-line no-console
                        console.log(`Invalid index to scroll to in Virtualizer: "${scrollToIndex}"`);
                    }
                }
            }
        }, [virtualizer, scrollToIndex]);

        // Notify parent of total size changes
        useEffect(() => {
            onTotalSizeChange?.(virtualizer.getTotalSize());
        }, [items, onTotalSizeChange, virtualizer]);

        const virtualItems = virtualizer.getVirtualItems();

        // Using inline styles for performance reasons
        // This avoids styled-components re-rendering overhead
        const scrollContainerStyle: CSSProperties = {
            overflow: height === 'fit-content' ? 'hidden' : 'auto',
            height: typeof height === 'number' ? `${height}px` : height,
        };

        const virtualizerRootStyle: CSSProperties = {
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
        };

        const virtualizerItemsStyle: CSSProperties = {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
            boxSizing: 'border-box',
        };

        return (
            <div ref={parentRef} style={scrollContainerStyle} data-test-id="virtualizer-container">
                <div style={virtualizerRootStyle} data-test-id="virtualizer-root">
                    <div
                        style={virtualizerItemsStyle}
                        data-test-id="virtualizer-items"
                        // This attribute is used for styling targeting from other components (e.g. SelectList)
                        data-component={VIRTUALIZER_ITEMS_COMPONENT}
                    >
                        {virtualItems.map((virtualItem) => (
                            <div
                                key={virtualItem.key.toString()}
                                data-index={virtualItem.index}
                                ref={virtualizer.measureElement}
                                data-test-id={`virtualizer-item-${virtualItem.index}`}
                            >
                                {items[virtualItem.index]}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }
);
